import {
  DataGrid,
  GridColDef,
  LoadingSpinner,
  MediaCardItem,
  MediaListItem,
  ModalOpenProcess,
  TagChips,
  useFormatting,
} from "@msys/ui";
import {
  BugReport as BugReportIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  Stack,
  TablePagination,
  Tooltip,
  Typography,
} from "@mui/material";
import type { GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  OrganisationQuoteTemplatesSortBy,
  OrganisationQuoteTemplatesSorting,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { TeamsCell } from "../teams/TeamsCell.js";
import { useCardsNumber } from "../../commons/hooks/useCardsNumber.js";
import { CollectionView } from "../../commons/hooks/useCollectionView.js";
import { PaginationModel } from "../../commons/pagination/usePaginationParams.js";
import { RESULTS_PER_PAGE_OPTIONS } from "../../constants.js";
import { TemplateQuotes_TemplateFragment } from "../../main-routes/templates/quote/TemplateQuotes.generated.js";
import { buildDocPath } from "../../utils.js";
import { usePropertyHelpers } from "../doc-items/usePropertyHelpers.js";
import { useProjectTypes } from "../projects/useProjectTypes.js";
import { PropertyValueChips } from "../properties/PropertyValueChips.js";
import { useDataGridStateStore } from "../users/useDataGridStateStore.js";
import { computeTemplateRelevantProperties } from "./helpers.js";
import { QuoteTemplateSubscribeButton } from "./quote/buttons/QuoteTemplateSubscribeButton.js";
import { ShowTemplateVariantProtocolModal } from "./quote/ShowTemplateVariantProtocolModal.js";
import { TemplateQuotePublishingStatus } from "./quote/TemplateQuotePublishingStatus.js";
import { useTemplateQuoteStatuses } from "./quote/TemplateQuoteStatuses.js";
import { useTemplateQuoteWarnings } from "./quote/TemplateQuoteWarnings.js";
import {
  hasTemplateTypeOrPropsInfo,
  TemplateTypeAndPropsInfo,
} from "./quote/TemplateTypeAndPropsInfo.js";
import { useFetchUpdateProtocolEntries } from "./quote/useTemplateVariantUpdateProtocol.js";
import { useTypeOfTemplates } from "./quote/useTypeOfTemplates.js";

type Props = {
  pathToDocList: string;
  showSubscribedColumn?: boolean;
  showTeamColumn?: boolean;
  activeView: CollectionView;
  sorting: OrganisationQuoteTemplatesSorting[];
  setSorting: (newSorting: OrganisationQuoteTemplatesSorting[]) => void;
  paginationModel: PaginationModel;
  setPaginationModel: (newPaginationModel: PaginationModel) => void;
  selectedItems: TemplateQuotes_TemplateFragment[];
  setSelectedItems: React.Dispatch<
    React.SetStateAction<TemplateQuotes_TemplateFragment[]>
  >;
  highlightedPropertyKeys?: string[] | undefined;
  items: TemplateQuotes_TemplateFragment[];
  totalCount: number;
  loading: boolean;
};

export function TemplateDocumentsList({
  items,
  totalCount,
  loading,
  pathToDocList,
  showSubscribedColumn = false,
  showTeamColumn = false,
  activeView,
  highlightedPropertyKeys,
  sorting,
  setSorting,
  paginationModel,
  setPaginationModel,
  selectedItems,
  setSelectedItems,
}: Props) {
  const navigate = useNavigate();

  const viewer = useUserData().currentUser!;
  const { t } = useTranslate([
    "Templates",
    "Global",
    "TemplatesSharing",
    "Quote",
  ]);
  const { getFormattedDate, getFormattedPrice } = useFormatting();
  const { getBoolLabel } = usePropertyHelpers();
  const { typeOfTemplatesLabels } = useTypeOfTemplates();
  const { projectTypeLabels } = useProjectTypes();
  const { spacing, sizeMeasureRef, columns } = useCardsNumber();

  const { getTemplateQuoteWarningIcon } = useTemplateQuoteWarnings();
  const { getTemplateQuoteStatusIcons } = useTemplateQuoteStatuses();

  function buildTemplateItemMeta(item: TemplateQuotes_TemplateFragment) {
    const statusIcons: React.ReactNode[] = [
      ...getTemplateQuoteStatusIcons(item),
      getTemplateQuoteWarningIcon(`${pathToDocList}/${item.id}`, item),
    ].filter(Boolean);

    const props = computeTemplateRelevantProperties({
      rootItemProps: item.rootItem.props2,
      templateTypeProps:
        item.implementsTemplateType?.templateType?.props2 ?? [],
    });

    const Info = hasTemplateTypeOrPropsInfo({
      templateTypeTitle: item.implementsTemplateType?.templateType.title,
      props,
    }) ? (
      <TemplateTypeAndPropsInfo
        templateTypeTitle={item.implementsTemplateType?.templateType.title}
        props={props}
      />
    ) : null;

    return { statusIcons, Info };
  }

  const stateStore = useDataGridStateStore("TemplateQuotesList");

  const { checkUpdateProtocol, updateVersionProtocolProcessRef } =
    useFetchUpdateProtocolEntries();

  const quoteTemplateColumns = React.useMemo(
    (): GridColDef<TemplateQuotes_TemplateFragment>[] => [
      {
        field: "title",
        headerName: t("Title", {
          ns: "Templates",
        }),
        flex: 2,
        minWidth: 100,
        renderCell: ({ row: template }) => template.title,
      },
      {
        field: "implementsTemplateType",
        headerName: t("Template type", {
          ns: "Templates",
        }),
        flex: 1,
        minWidth: 50,
        sortable: false,
        renderCell: ({ row: template }) =>
          template.implementsTemplateType?.templateType?.title ?? "",
      },
      {
        field: "templateTypeProperties",
        headerName: t("Template type properties", {
          ns: "Templates",
        }),
        flex: 3,
        minWidth: 150,
        sortable: false,
        renderCell: ({ row: template }) => {
          const props = computeTemplateRelevantProperties({
            rootItemProps: template.rootItem.props2,
            templateTypeProps:
              template.implementsTemplateType?.templateType?.props2 ?? [],
          });
          return (
            <PropertyValueChips
              highlightedKeys={highlightedPropertyKeys}
              props={props}
              wrap={false}
            />
          );
        },
      },
      {
        field: "category",
        headerName: t("Template category", {
          ns: "Templates",
        }),
        flex: 1,
        minWidth: 50,
        sortable: false,
        renderCell: ({ row: template }) => template.category?.name ?? "",
      },
      {
        field: "typeOfTemplate",
        headerName: t("Type of template", { ns: "Templates" }),
        flex: 1,
        minWidth: 50,
        sortable: false,
        renderCell: ({ row: template }) =>
          template.typeOfTemplate
            ? typeOfTemplatesLabels[template.typeOfTemplate]
            : "",
      },
      {
        field: "typeOfProject",
        headerName: t("Type of project", { ns: "Templates" }),
        flex: 1,
        minWidth: 50,
        sortable: false,
        renderCell: ({ row: template }) =>
          template.typeOfProject && template.typeOfProject.length > 0
            ? template.typeOfProject
                .map(top => projectTypeLabels[top])
                .join(", ")
            : "",
      },
      {
        field: "discountedPrice",
        headerName: t("Price", {
          ns: "Templates",
        }),
        width: 120,
        headerAlign: "right",
        align: "right",
        renderCell: ({ row: template }) =>
          getFormattedPrice(template.proposedCalculation?.priceNetTotal || 0),
      },
      {
        field: "tags",
        headerName: t("Tags", {
          ns: "Templates",
        }),
        flex: 1.5,
        minWidth: 100,
        sortable: false,
        renderCell: ({ row: template }) => (
          <TagChips tags={template.customTags} wrap={false} />
        ),
      },
      {
        field: "publishingStatus",
        headerName: t("Publishing", {
          ns: "Templates",
        }),
        flex: 0.75,
        minWidth: 100,
        sortable: false,
        renderCell: ({ row: template }) => (
          <TemplateQuotePublishingStatus
            doc={template}
            variant="body2"
            IconProps={{ fontSize: "small" }}
          />
        ),
      },
      {
        field: "createdAt",
        headerName: t("Created", {
          ns: "Templates",
        }),
        width: 120,
        renderCell: ({ row: template }) => getFormattedDate(template.createdAt),
      },
      ...(showSubscribedColumn
        ? ([
            {
              field: "subscribed",
              headerName: t("Subscribed", {
                ns: "Templates",
              }),
              flex: 0.75,
              minWidth: 140,
              sortable: false,
              renderCell: ({ row: template }) => {
                if (
                  template.owningSystemOrganisationId === viewer.organisation.id
                ) {
                  return null;
                }

                return (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      alignItems: "center",
                    }}
                  >
                    {!template.templateSharing?.subscribedToTemplate ? (
                      <RestrictedByOrganisationPermissionWithDebug permission="template_share_create">
                        <QuoteTemplateSubscribeButton
                          doc={template}
                          size="extra-small"
                          onAfterUpdate={async (opts, applyOverrides) => {
                            if (applyOverrides) {
                              await checkUpdateProtocol(opts);
                            }
                          }}
                        />
                      </RestrictedByOrganisationPermissionWithDebug>
                    ) : template.templateSharing?.subscribedToTemplate
                        ?.updateAvailable ? (
                      <Tooltip
                        title={t("New version available", { ns: "Templates" })}
                      >
                        <WarningIcon color="warning" />
                      </Tooltip>
                    ) : (
                      <CheckCircleIcon color="success" />
                    )}
                  </Stack>
                );
              },
            },
            {
              field: "hasOverridesDefined",
              headerName: t("Has overrides", { ns: "Templates" }),
              sortable: false,
              valueGetter(_value, template) {
                return template.hasOverridesDefined;
              },
              valueFormatter(value) {
                return getBoolLabel(value);
              },
            },
          ] as GridColDef<TemplateQuotes_TemplateFragment>[])
        : []),
      ...(showTeamColumn && viewer.organisation.capabilities.includes("teams")
        ? ([
            {
              field: "team",
              headerName: t("Team", { ns: "Quote" }),
              minWidth: 100,
              flex: 1,
              sortable: false,
              renderCell: ({ row: template }) => (
                <TeamsCell teams={template.team ? [template.team] : []} />
              ),
            },
          ] as GridColDef<TemplateQuotes_TemplateFragment>[])
        : []),
      {
        field: "actions",
        headerName: "",
        sortable: false,
        maxWidth: 80,
        minWidth: 40,
        renderCell: ({ row: template }) => (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              alignItems: "center",
            }}
          >
            <RestrictedByOrganisationPermissionWithDebug permission="template_document_update">
              <IconButton
                component={Link}
                to={
                  buildDocPath(
                    `${pathToDocList}/${template.id}`,
                    template.resolvedAsReadModelVersionNumber ?? null
                  ) + "/troubleshoot"
                }
                onClick={event => {
                  event.stopPropagation();
                }}
                color={"primary"}
                sx={{ marginLeft: "-10px" }}
              >
                <BugReportIcon />
              </IconButton>
            </RestrictedByOrganisationPermissionWithDebug>
            {/*<RestrictedByOrganisationPermissionWithDebug permission="template_document_update">
              {template.resolvedAsReadModelVersionNumber ? (
                <ShowTemplateVariantProtocolButton
                  templateId={template.id}
                  versionNumber={template.resolvedAsReadModelVersionNumber}
                />
              ) : null}
            </RestrictedByOrganisationPermissionWithDebug>*/}
          </Stack>
        ),
      },
    ],
    [
      t,
      showSubscribedColumn,
      showTeamColumn,
      viewer.organisation.capabilities,
      highlightedPropertyKeys,
      typeOfTemplatesLabels,
      projectTypeLabels,
      getFormattedPrice,
      getFormattedDate,
      viewer.organisation.id,
      checkUpdateProtocol,
      getBoolLabel,
      pathToDocList,
    ]
  );

  const rowSelectionModel = React.useMemo(
    () =>
      ({
        type: "include",
        ids: new Set(selectedItems.map(item => item.id)),
      }) satisfies GridRowSelectionModel,
    [selectedItems]
  );

  switch (activeView) {
    case "table":
      return (
        <>
          <DataGrid<TemplateQuotes_TemplateFragment>
            stateStore={stateStore}
            loading={loading}
            hideFooter={totalCount === 0}
            columns={quoteTemplateColumns}
            rows={items}
            sortModel={sorting.map(s => ({
              field: s.column,
              sort: s.direction,
            }))}
            onSortModelChange={newModel => {
              setSorting(
                newModel.map(({ field, sort }) => ({
                  column: field as OrganisationQuoteTemplatesSortBy,
                  direction: sort ?? "asc",
                }))
              );
            }}
            onRowClick={({ row: template }) => {
              return navigate(
                buildDocPath(
                  `${pathToDocList}/${template.id}`,
                  template.resolvedAsReadModelVersionNumber ?? null
                )
              );
            }}
            paginationModel={paginationModel}
            onPaginationModelChange={newPaginationModel => {
              setPaginationModel(newPaginationModel);
            }}
            pageSizeOptions={RESULTS_PER_PAGE_OPTIONS}
            rowCount={totalCount}
            checkboxSelection
            onRowSelectionModelChange={model => {
              const toAdd = Array.from(model.ids)
                .filter(id => !selectedItems.find(item => item.id === id))
                .map(id => items.find(item => item.id === id))
                .filter(item => item) as TemplateQuotes_TemplateFragment[]; // FIXME
              setSelectedItems([
                ...selectedItems.filter(item => model.ids.has(item.id)),
                ...toAdd,
              ]);
            }}
            disableRowSelectionExcludeModel
            rowSelectionModel={rowSelectionModel}
            localeText={{
              noRowsLabel: t("There are no quote templates.", {
                ns: "Templates",
              }),
              paginationRowsPerPage: t("Rows per page", {
                ns: "Global",
              }),
            }}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  typeOfTemplate: false,
                  typeOfProject: false,
                },
              },
            }}
          />
          <ModalOpenProcess
            ref={updateVersionProtocolProcessRef}
            Modal={ShowTemplateVariantProtocolModal}
            modalProps={{
              pathToDocList,
              toVersionNumber: 0,
              entries: [],
            }}
          />
        </>
      );
    case "list": {
      if (loading) {
        return <LoadingSpinner />;
      }
      if (!items.length) {
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "grey.600",
              }}
            >
              {t("There are no quote templates.", { ns: "Templates" })}
            </Typography>
          </Box>
        );
      }
      return (
        <Stack spacing={1}>
          {items.map(item => {
            const { statusIcons, Info } = buildTemplateItemMeta(item);

            return (
              <MediaListItem
                key={item.id}
                component={Link}
                to={buildDocPath(
                  `${pathToDocList}/${item.id}`,
                  item.resolvedAsReadModelVersionNumber ?? null
                )}
                title={item.title}
                description={item.description}
                price={getFormattedPrice(
                  item.proposedCalculation?.priceNetTotal || 0
                )}
                imageUrl={item.shopCatalogProductImage?.url}
                Tags={
                  item.customTags.length > 0 ? (
                    <TagChips tags={item.customTags} wrap={false} />
                  ) : null
                }
                Info={Info}
                StatusIcons={statusIcons}
              />
            );
          })}
          <TablePagination
            component="div"
            count={totalCount}
            page={paginationModel.page}
            onPageChange={(_event, newPage) => {
              setPaginationModel({
                pageSize: paginationModel.pageSize,
                page: newPage,
              });
            }}
            rowsPerPage={paginationModel.pageSize}
            onRowsPerPageChange={event => {
              setPaginationModel({
                pageSize: parseInt(event.target.value, 10),
                page: 0,
              });
            }}
            labelRowsPerPage={t("Per page:", {
              ns: "Global",
            })}
          />
        </Stack>
      );
    }
    case "gallery": {
      return (
        <Stack spacing={1} ref={sizeMeasureRef}>
          {loading ? (
            <LoadingSpinner />
          ) : !items.length ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                p: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "grey.600",
                }}
              >
                {t("There are no quote templates.", { ns: "Templates" })}
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  overflow: "auto",
                  flex: 1,
                }}
              >
                <Grid container spacing={spacing}>
                  {items.map(item => {
                    const { statusIcons, Info } = buildTemplateItemMeta(item);

                    return (
                      <Grid key={item.id} size={columns}>
                        <MediaCardItem
                          key={item.id}
                          component={Link}
                          to={buildDocPath(
                            `${pathToDocList}/${item.id}`,
                            item.resolvedAsReadModelVersionNumber ?? null
                          )}
                          title={item.title}
                          description={item.description}
                          price={getFormattedPrice(
                            item.proposedCalculation?.priceNetTotal || 0
                          )}
                          imageUrl={item.shopCatalogProductImage?.url}
                          Tags={
                            item.customTags.length > 0 ? (
                              <TagChips tags={item.customTags} wrap={true} />
                            ) : null
                          }
                          Info={Info}
                          StatusIcons={statusIcons}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
              <TablePagination
                component="div"
                count={totalCount}
                page={paginationModel.page}
                onPageChange={(_event, newPage) => {
                  setPaginationModel({
                    pageSize: paginationModel.pageSize,
                    page: newPage,
                  });
                }}
                rowsPerPage={paginationModel.pageSize}
                onRowsPerPageChange={event => {
                  setPaginationModel({
                    pageSize: parseInt(event.target.value, 10),
                    page: 0,
                  });
                }}
                labelRowsPerPage={t("Per page:", {
                  ns: "Global",
                })}
              />
            </>
          )}
        </Stack>
      );
    }
  }
}
